/**
 * GLAMRDiP routine-cards — Apply / Remove section.
 * Joel 2026-04-29 (v2): tiles are decorative inline loops only. Modal
 * handler retired — the click-to-open-full-video behaviour was removed
 * from markup + this script. Single responsibility now: lazy-load + play
 * the in-card preview videos when ≥50% in view, pause when out.
 *
 * Page-speed protocol:
 *   - Markup ships with preload="none" + poster → 0 video bytes loaded
 *     until the IO upgrades preload to "auto" + .load() on first
 *     intersect.
 *   - Pause off-screen → no CPU/battery on cards the user has scrolled
 *     past.
 *   - prefers-reduced-motion bails out: poster only, no autoplay.
 *
 * Defensive: try/catch around play() (autoplay can be blocked by
 * browser policy even with muted/playsinline in some contexts).
 */
(() => {
  'use strict';
  try {
    // Joel 2026-05-02 (multi-instance fix): the prior `querySelector(
    // '.gd-apply-remove')` returned only the FIRST occurrence on the
    // page. The kit PDP can now host multiple Apply / remove sections
    // (apply-remove-steps + apply-remove-steps-2 clone, OR the same
    // section added twice via "Add section"). Both instances render
    // with the inner class .gd-apply-remove (schema `class` field
    // only affects Shopify's outer wrapper), so the singular query
    // missed every video on the second section → those videos kept
    // preload="none" forever and never auto-played, rendering as
    // empty grey rectangles. Fix: query every preview video across
    // every section in one pass — the single IO below observes all
    // of them and per-element state (preload, dataset.gdRoutinePlayed)
    // keeps each video independent.
    const previews = document.querySelectorAll('.gd-apply-remove [data-gd-routine-preview]');
    if (!previews.length) return;

    const reducedMotion = window.matchMedia &&
                          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      previews.forEach((v) => {
        v.removeAttribute('autoplay');
        try { v.pause(); } catch {}
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      previews.forEach((v) => {
        v.preload = 'auto';
        try { v.load(); } catch {}
        v.play().catch(() => {});
      });
      return;
    }

    /**
     * iOS Safari edge case: calling .play() immediately after .load()
     * fails silently because metadata hasn't been fetched yet. Spec-wise
     * .play() should queue until ready, but iOS occasionally rejects
     * the promise without queueing. Mitigation: wait for the
     * `loadedmetadata` event before the first play() per video. After
     * the first play() succeeds, subsequent intersections call play()
     * directly (the browser has already buffered enough to resume).
     *
     * Joel 2026-04-30 (perf 2.2): autoplay attribute removed from the
     * <video> markup so the browser respects preload="none". The play()
     * call works without the autoplay attribute because the video is
     * muted+playsinline (browser policy permits programmatic play in
     * that combination).
     */
    // Joel 2026-05-02: surface iOS autoplay failures so a real-device
    // trace shows what's blocking. Previously every play() rejection
    // was swallowed silently — debugging required guessing. Logs the
    // error name (NotAllowedError, AbortError, etc.) + the video src
    // so we can tell which iOS restriction is firing (low-power mode,
    // missing autoplay attr, missing metadata, etc.). One log per video
    // per error — observers are idempotent so this won't spam.
    const playWithDiag = (v) => {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.catch((err) => {
          if (!v.dataset.gdRoutinePlayFail) {
            v.dataset.gdRoutinePlayFail = '1';
            // eslint-disable-next-line no-console
            console.warn('[gd-routine-cards] play() failed:', err.name, '—', v.currentSrc || (v.querySelector('source') || {}).src);
          }
        });
      }
    };
    const playOnce = (v) => {
      if (v.dataset.gdRoutinePlayed === '1') {
        playWithDiag(v);
        return;
      }
      const onMeta = () => {
        v.removeEventListener('loadedmetadata', onMeta);
        v.removeEventListener('canplay', onMeta);
        v.dataset.gdRoutinePlayed = '1';
        playWithDiag(v);
      };
      // readyState ≥ 1 = HAVE_METADATA. If already there (e.g. cached
      // from a prior visit), fire the play directly.
      if (v.readyState >= 1) { onMeta(); return; }
      v.addEventListener('loadedmetadata', onMeta, { once: true });
      v.addEventListener('canplay',        onMeta, { once: true });
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (entry.isIntersecting) {
          if (v.preload === 'none') {
            v.preload = 'auto';
            try { v.load(); } catch {}
          }
          playOnce(v);
        } else {
          try { v.pause(); } catch {}
        }
      });
    }, {
      threshold: 0.5,
      // Joel 2026-05-02: rootMargin bumped 50 → 500px so video metadata
      // starts loading before the section enters viewport. With 50px,
      // users saw "empty card → loading → playing" sequence on scroll.
      // 500px = ~1-2s head start at typical scroll velocity — by the
      // time the section is visible, first frame paints immediately
      // on play(). Same fix applied to gd-product-video.js +
      // accessory-how-to-use.liquid for site-wide consistency.
      rootMargin: '500px 0px',
    });

    previews.forEach((v) => io.observe(v));
  } catch (e) {
    console.warn('[gd-routine-cards] init failed:', e);
  }
})();
